import { __decorate, __extends } from "tslib";
import { serialize, SerializationHelper, serializeAsColor3, expandToProperty } from "../Misc/decorators";
import { Vector3 } from "../Maths/math.vector";
import { Color3, TmpColors } from "../Maths/math.color";
import { Node } from "../node";
import { UniformBuffer } from "../Materials/uniformBuffer";
import { _TypeStore } from '../Misc/typeStore';
/**
 * Base class of all the lights in Babylon. It groups all the generic information about lights.
 * Lights are used, as you would expect, to affect how meshes are seen, in terms of both illumination and colour.
 * All meshes allow light to pass through them unless shadow generation is activated. The default number of lights allowed is four but this can be increased.
 */
var Light = /** @class */ (function (_super) {
    __extends(Light, _super);
    /**
     * Creates a Light object in the scene.
     * Documentation : https://doc.babylonjs.com/babylon101/lights
     * @param name The firendly name of the light
     * @param scene The scene the light belongs too
     */
    function Light(name, scene) {
        var _this = _super.call(this, name, scene) || this;
        /**
         * Diffuse gives the basic color to an object.
         */
        _this.diffuse = new Color3(1.0, 1.0, 1.0);
        /**
         * Specular produces a highlight color on an object.
         * Note: This is note affecting PBR materials.
         */
        _this.specular = new Color3(1.0, 1.0, 1.0);
        /**
         * Defines the falloff type for this light. This lets overrriding how punctual light are
         * falling off base on range or angle.
         * This can be set to any values in Light.FALLOFF_x.
         *
         * Note: This is only useful for PBR Materials at the moment. This could be extended if required to
         * other types of materials.
         */
        _this.falloffType = Light.FALLOFF_DEFAULT;
        /**
         * Strength of the light.
         * Note: By default it is define in the framework own unit.
         * Note: In PBR materials the intensityMode can be use to chose what unit the intensity is defined in.
         */
        _this.intensity = 1.0;
        _this._range = Number.MAX_VALUE;
        _this._inverseSquaredRange = 0;
        /**
         * Cached photometric scale default to 1.0 as the automatic intensity mode defaults to 1.0 for every type
         * of light.
         */
        _this._photometricScale = 1.0;
        _this._intensityMode = Light.INTENSITYMODE_AUTOMATIC;
        _this._radius = 0.00001;
        /**
         * Defines the rendering priority of the lights. It can help in case of fallback or number of lights
         * exceeding the number allowed of the materials.
         */
        _this.renderPriority = 0;
        _this._shadowEnabled = true;
        _this._excludeWithLayerMask = 0;
        _this._includeOnlyWithLayerMask = 0;
        _this._lightmapMode = 0;
        /**
         * @hidden Internal use only.
         */
        _this._excludedMeshesIds = new Array();
        /**
         * @hidden Internal use only.
         */
        _this._includedOnlyMeshesIds = new Array();
        /** @hidden */
        _this._isLight = true;
        _this.getScene().addLight(_this);
        _this._uniformBuffer = new UniformBuffer(_this.getScene().getEngine());
        _this._buildUniformLayout();
        _this.includedOnlyMeshes = new Array();
        _this.excludedMeshes = new Array();
        _this._resyncMeshes();
        return _this;
    }
    Object.defineProperty(Light.prototype, "range", {
        /**
         * Defines how far from the source the light is impacting in scene units.
         * Note: Unused in PBR material as the distance light falloff is defined following the inverse squared falloff.
         */
        get: function () {
            return this._range;
        },
        /**
         * Defines how far from the source the light is impacting in scene units.
         * Note: Unused in PBR material as the distance light falloff is defined following the inverse squared falloff.
         */
        set: function (value) {
            this._range = value;
            this._inverseSquaredRange = 1.0 / (this.range * this.range);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "intensityMode", {
        /**
         * Gets the photometric scale used to interpret the intensity.
         * This is only relevant with PBR Materials where the light intensity can be defined in a physical way.
         */
        get: function () {
            return this._intensityMode;
        },
        /**
         * Sets the photometric scale used to interpret the intensity.
         * This is only relevant with PBR Materials where the light intensity can be defined in a physical way.
         */
        set: function (value) {
            this._intensityMode = value;
            this._computePhotometricScale();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "radius", {
        /**
         * Gets the light radius used by PBR Materials to simulate soft area lights.
         */
        get: function () {
            return this._radius;
        },
        /**
         * sets the light radius used by PBR Materials to simulate soft area lights.
         */
        set: function (value) {
            this._radius = value;
            this._computePhotometricScale();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "shadowEnabled", {
        /**
         * Gets wether or not the shadows are enabled for this light. This can help turning off/on shadow without detaching
         * the current shadow generator.
         */
        get: function () {
            return this._shadowEnabled;
        },
        /**
         * Sets wether or not the shadows are enabled for this light. This can help turning off/on shadow without detaching
         * the current shadow generator.
         */
        set: function (value) {
            if (this._shadowEnabled === value) {
                return;
            }
            this._shadowEnabled = value;
            this._markMeshesAsLightDirty();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "includedOnlyMeshes", {
        /**
         * Gets the only meshes impacted by this light.
         */
        get: function () {
            return this._includedOnlyMeshes;
        },
        /**
         * Sets the only meshes impacted by this light.
         */
        set: function (value) {
            this._includedOnlyMeshes = value;
            this._hookArrayForIncludedOnly(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "excludedMeshes", {
        /**
         * Gets the meshes not impacted by this light.
         */
        get: function () {
            return this._excludedMeshes;
        },
        /**
         * Sets the meshes not impacted by this light.
         */
        set: function (value) {
            this._excludedMeshes = value;
            this._hookArrayForExcluded(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "excludeWithLayerMask", {
        /**
         * Gets the layer id use to find what meshes are not impacted by the light.
         * Inactive if 0
         */
        get: function () {
            return this._excludeWithLayerMask;
        },
        /**
         * Sets the layer id use to find what meshes are not impacted by the light.
         * Inactive if 0
         */
        set: function (value) {
            this._excludeWithLayerMask = value;
            this._resyncMeshes();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "includeOnlyWithLayerMask", {
        /**
         * Gets the layer id use to find what meshes are impacted by the light.
         * Inactive if 0
         */
        get: function () {
            return this._includeOnlyWithLayerMask;
        },
        /**
         * Sets the layer id use to find what meshes are impacted by the light.
         * Inactive if 0
         */
        set: function (value) {
            this._includeOnlyWithLayerMask = value;
            this._resyncMeshes();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Light.prototype, "lightmapMode", {
        /**
         * Gets the lightmap mode of this light (should be one of the constants defined by Light.LIGHTMAP_x)
         */
        get: function () {
            return this._lightmapMode;
        },
        /**
         * Sets the lightmap mode of this light (should be one of the constants defined by Light.LIGHTMAP_x)
         */
        set: function (value) {
            if (this._lightmapMode === value) {
                return;
            }
            this._lightmapMode = value;
            this._markMeshesAsLightDirty();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Sets the passed Effect "effect" with the Light textures.
     * @param effect The effect to update
     * @param lightIndex The index of the light in the effect to update
     * @returns The light
     */
    Light.prototype.transferTexturesToEffect = function (effect, lightIndex) {
        // Do nothing by default.
        return this;
    };
    /**
     * Binds the lights information from the scene to the effect for the given mesh.
     * @param lightIndex Light index
     * @param scene The scene where the light belongs to
     * @param effect The effect we are binding the data to
     * @param useSpecular Defines if specular is supported
     * @param rebuildInParallel Specifies whether the shader is rebuilding in parallel
     */
    Light.prototype._bindLight = function (lightIndex, scene, effect, useSpecular, rebuildInParallel) {
        if (rebuildInParallel === void 0) { rebuildInParallel = false; }
        var iAsString = lightIndex.toString();
        var needUpdate = false;
        if (rebuildInParallel && this._uniformBuffer._alreadyBound) {
            return;
        }
        this._uniformBuffer.bindToEffect(effect, "Light" + iAsString);
        if (this._renderId !== scene.getRenderId() || !this._uniformBuffer.useUbo) {
            this._renderId = scene.getRenderId();
            var scaledIntensity = this.getScaledIntensity();
            this.transferToEffect(effect, iAsString);
            this.diffuse.scaleToRef(scaledIntensity, TmpColors.Color3[0]);
            this._uniformBuffer.updateColor4("vLightDiffuse", TmpColors.Color3[0], this.range, iAsString);
            if (useSpecular) {
                this.specular.scaleToRef(scaledIntensity, TmpColors.Color3[1]);
                this._uniformBuffer.updateColor4("vLightSpecular", TmpColors.Color3[1], this.radius, iAsString);
            }
            needUpdate = true;
        }
        // Textures might still need to be rebound.
        this.transferTexturesToEffect(effect, iAsString);
        // Shadows
        if (scene.shadowsEnabled && this.shadowEnabled) {
            var shadowGenerator = this.getShadowGenerator();
            if (shadowGenerator) {
                shadowGenerator.bindShadowLight(iAsString, effect);
                needUpdate = true;
            }
        }
        if (needUpdate) {
            this._uniformBuffer.update();
        }
    };
    /**
     * Returns the string "Light".
     * @returns the class name
     */
    Light.prototype.getClassName = function () {
        return "Light";
    };
    /**
     * Converts the light information to a readable string for debug purpose.
     * @param fullDetails Supports for multiple levels of logging within scene loading
     * @returns the human readable light info
     */
    Light.prototype.toString = function (fullDetails) {
        var ret = "Name: " + this.name;
        ret += ", type: " + (["Point", "Directional", "Spot", "Hemispheric"])[this.getTypeID()];
        if (this.animations) {
            for (var i = 0; i < this.animations.length; i++) {
                ret += ", animation[0]: " + this.animations[i].toString(fullDetails);
            }
        }
        if (fullDetails) {
        }
        return ret;
    };
    /** @hidden */
    Light.prototype._syncParentEnabledState = function () {
        _super.prototype._syncParentEnabledState.call(this);
        if (!this.isDisposed()) {
            this._resyncMeshes();
        }
    };
    /**
     * Set the enabled state of this node.
     * @param value - the new enabled state
     */
    Light.prototype.setEnabled = function (value) {
        _super.prototype.setEnabled.call(this, value);
        this._resyncMeshes();
    };
    /**
     * Returns the Light associated shadow generator if any.
     * @return the associated shadow generator.
     */
    Light.prototype.getShadowGenerator = function () {
        return this._shadowGenerator;
    };
    /**
     * Returns a Vector3, the absolute light position in the World.
     * @returns the world space position of the light
     */
    Light.prototype.getAbsolutePosition = function () {
        return Vector3.Zero();
    };
    /**
     * Specifies if the light will affect the passed mesh.
     * @param mesh The mesh to test against the light
     * @return true the mesh is affected otherwise, false.
     */
    Light.prototype.canAffectMesh = function (mesh) {
        if (!mesh) {
            return true;
        }
        if (this.includedOnlyMeshes && this.includedOnlyMeshes.length > 0 && this.includedOnlyMeshes.indexOf(mesh) === -1) {
            return false;
        }
        if (this.excludedMeshes && this.excludedMeshes.length > 0 && this.excludedMeshes.indexOf(mesh) !== -1) {
            return false;
        }
        if (this.includeOnlyWithLayerMask !== 0 && (this.includeOnlyWithLayerMask & mesh.layerMask) === 0) {
            return false;
        }
        if (this.excludeWithLayerMask !== 0 && this.excludeWithLayerMask & mesh.layerMask) {
            return false;
        }
        return true;
    };
    /**
     * Sort function to order lights for rendering.
     * @param a First Light object to compare to second.
     * @param b Second Light object to compare first.
     * @return -1 to reduce's a's index relative to be, 0 for no change, 1 to increase a's index relative to b.
     */
    Light.CompareLightsPriority = function (a, b) {
        //shadow-casting lights have priority over non-shadow-casting lights
        //the renderPrioirty is a secondary sort criterion
        if (a.shadowEnabled !== b.shadowEnabled) {
            return (b.shadowEnabled ? 1 : 0) - (a.shadowEnabled ? 1 : 0);
        }
        return b.renderPriority - a.renderPriority;
    };
    /**
     * Releases resources associated with this node.
     * @param doNotRecurse Set to true to not recurse into each children (recurse into each children by default)
     * @param disposeMaterialAndTextures Set to true to also dispose referenced materials and textures (false by default)
     */
    Light.prototype.dispose = function (doNotRecurse, disposeMaterialAndTextures) {
        if (disposeMaterialAndTextures === void 0) { disposeMaterialAndTextures = false; }
        if (this._shadowGenerator) {
            this._shadowGenerator.dispose();
            this._shadowGenerator = null;
        }
        // Animations
        this.getScene().stopAnimation(this);
        // Remove from meshes
        for (var _i = 0, _a = this.getScene().meshes; _i < _a.length; _i++) {
            var mesh = _a[_i];
            mesh._removeLightSource(this, true);
        }
        this._uniformBuffer.dispose();
        // Remove from scene
        this.getScene().removeLight(this);
        _super.prototype.dispose.call(this, doNotRecurse, disposeMaterialAndTextures);
    };
    /**
     * Returns the light type ID (integer).
     * @returns The light Type id as a constant defines in Light.LIGHTTYPEID_x
     */
    Light.prototype.getTypeID = function () {
        return 0;
    };
    /**
     * Returns the intensity scaled by the Photometric Scale according to the light type and intensity mode.
     * @returns the scaled intensity in intensity mode unit
     */
    Light.prototype.getScaledIntensity = function () {
        return this._photometricScale * this.intensity;
    };
    /**
     * Returns a new Light object, named "name", from the current one.
     * @param name The name of the cloned light
     * @param newParent The parent of this light, if it has one
     * @returns the new created light
     */
    Light.prototype.clone = function (name, newParent) {
        if (newParent === void 0) { newParent = null; }
        var constructor = Light.GetConstructorFromName(this.getTypeID(), name, this.getScene());
        if (!constructor) {
            return null;
        }
        var clonedLight = SerializationHelper.Clone(constructor, this);
        if (newParent) {
            clonedLight.parent = newParent;
        }
        clonedLight.setEnabled(this.isEnabled());
        return clonedLight;
    };
    /**
     * Serializes the current light into a Serialization object.
     * @returns the serialized object.
     */
    Light.prototype.serialize = function () {
        var serializationObject = SerializationHelper.Serialize(this);
        // Type
        serializationObject.type = this.getTypeID();
        // Parent
        if (this.parent) {
            serializationObject.parentId = this.parent.id;
        }
        // Inclusion / exclusions
        if (this.excludedMeshes.length > 0) {
            serializationObject.excludedMeshesIds = [];
            this.excludedMeshes.forEach(function (mesh) {
                serializationObject.excludedMeshesIds.push(mesh.id);
            });
        }
        if (this.includedOnlyMeshes.length > 0) {
            serializationObject.includedOnlyMeshesIds = [];
            this.includedOnlyMeshes.forEach(function (mesh) {
                serializationObject.includedOnlyMeshesIds.push(mesh.id);
            });
        }
        // Animations
        SerializationHelper.AppendSerializedAnimations(this, serializationObject);
        serializationObject.ranges = this.serializeAnimationRanges();
        return serializationObject;
    };
    /**
     * Creates a new typed light from the passed type (integer) : point light = 0, directional light = 1, spot light = 2, hemispheric light = 3.
     * This new light is named "name" and added to the passed scene.
     * @param type Type according to the types available in Light.LIGHTTYPEID_x
     * @param name The friendly name of the light
     * @param scene The scene the new light will belong to
     * @returns the constructor function
     */
    Light.GetConstructorFromName = function (type, name, scene) {
        var constructorFunc = Node.Construct("Light_Type_" + type, name, scene);
        if (constructorFunc) {
            return constructorFunc;
        }
        // Default to no light for none present once.
        return null;
    };
    /**
     * Parses the passed "parsedLight" and returns a new instanced Light from this parsing.
     * @param parsedLight The JSON representation of the light
     * @param scene The scene to create the parsed light in
     * @returns the created light after parsing
     */
    Light.Parse = function (parsedLight, scene) {
        var constructor = Light.GetConstructorFromName(parsedLight.type, parsedLight.name, scene);
        if (!constructor) {
            return null;
        }
        var light = SerializationHelper.Parse(constructor, parsedLight, scene);
        // Inclusion / exclusions
        if (parsedLight.excludedMeshesIds) {
            light._excludedMeshesIds = parsedLight.excludedMeshesIds;
        }
        if (parsedLight.includedOnlyMeshesIds) {
            light._includedOnlyMeshesIds = parsedLight.includedOnlyMeshesIds;
        }
        // Parent
        if (parsedLight.parentId) {
            light._waitingParentId = parsedLight.parentId;
        }
        // Falloff
        if (parsedLight.falloffType !== undefined) {
            light.falloffType = parsedLight.falloffType;
        }
        // Lightmaps
        if (parsedLight.lightmapMode !== undefined) {
            light.lightmapMode = parsedLight.lightmapMode;
        }
        // Animations
        if (parsedLight.animations) {
            for (var animationIndex = 0; animationIndex < parsedLight.animations.length; animationIndex++) {
                var parsedAnimation = parsedLight.animations[animationIndex];
                var internalClass = _TypeStore.GetClass("BABYLON.Animation");
                if (internalClass) {
                    light.animations.push(internalClass.Parse(parsedAnimation));
                }
            }
            Node.ParseAnimationRanges(light, parsedLight, scene);
        }
        if (parsedLight.autoAnimate) {
            scene.beginAnimation(light, parsedLight.autoAnimateFrom, parsedLight.autoAnimateTo, parsedLight.autoAnimateLoop, parsedLight.autoAnimateSpeed || 1.0);
        }
        return light;
    };
    Light.prototype._hookArrayForExcluded = function (array) {
        var _this = this;
        var oldPush = array.push;
        array.push = function () {
            var items = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                items[_i] = arguments[_i];
            }
            var result = oldPush.apply(array, items);
            for (var _a = 0, items_1 = items; _a < items_1.length; _a++) {
                var item = items_1[_a];
                item._resyncLightSource(_this);
            }
            return result;
        };
        var oldSplice = array.splice;
        array.splice = function (index, deleteCount) {
            var deleted = oldSplice.apply(array, [index, deleteCount]);
            for (var _i = 0, deleted_1 = deleted; _i < deleted_1.length; _i++) {
                var item = deleted_1[_i];
                item._resyncLightSource(_this);
            }
            return deleted;
        };
        for (var _i = 0, array_1 = array; _i < array_1.length; _i++) {
            var item = array_1[_i];
            item._resyncLightSource(this);
        }
    };
    Light.prototype._hookArrayForIncludedOnly = function (array) {
        var _this = this;
        var oldPush = array.push;
        array.push = function () {
            var items = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                items[_i] = arguments[_i];
            }
            var result = oldPush.apply(array, items);
            _this._resyncMeshes();
            return result;
        };
        var oldSplice = array.splice;
        array.splice = function (index, deleteCount) {
            var deleted = oldSplice.apply(array, [index, deleteCount]);
            _this._resyncMeshes();
            return deleted;
        };
        this._resyncMeshes();
    };
    Light.prototype._resyncMeshes = function () {
        for (var _i = 0, _a = this.getScene().meshes; _i < _a.length; _i++) {
            var mesh = _a[_i];
            mesh._resyncLightSource(this);
        }
    };
    /**
     * Forces the meshes to update their light related information in their rendering used effects
     * @hidden Internal Use Only
     */
    Light.prototype._markMeshesAsLightDirty = function () {
        for (var _i = 0, _a = this.getScene().meshes; _i < _a.length; _i++) {
            var mesh = _a[_i];
            if (mesh.lightSources.indexOf(this) !== -1) {
                mesh._markSubMeshesAsLightDirty();
            }
        }
    };
    /**
     * Recomputes the cached photometric scale if needed.
     */
    Light.prototype._computePhotometricScale = function () {
        this._photometricScale = this._getPhotometricScale();
        this.getScene().resetCachedMaterial();
    };
    /**
     * Returns the Photometric Scale according to the light type and intensity mode.
     */
    Light.prototype._getPhotometricScale = function () {
        var photometricScale = 0.0;
        var lightTypeID = this.getTypeID();
        //get photometric mode
        var photometricMode = this.intensityMode;
        if (photometricMode === Light.INTENSITYMODE_AUTOMATIC) {
            if (lightTypeID === Light.LIGHTTYPEID_DIRECTIONALLIGHT) {
                photometricMode = Light.INTENSITYMODE_ILLUMINANCE;
            }
            else {
                photometricMode = Light.INTENSITYMODE_LUMINOUSINTENSITY;
            }
        }
        //compute photometric scale
        switch (lightTypeID) {
            case Light.LIGHTTYPEID_POINTLIGHT:
            case Light.LIGHTTYPEID_SPOTLIGHT:
                switch (photometricMode) {
                    case Light.INTENSITYMODE_LUMINOUSPOWER:
                        photometricScale = 1.0 / (4.0 * Math.PI);
                        break;
                    case Light.INTENSITYMODE_LUMINOUSINTENSITY:
                        photometricScale = 1.0;
                        break;
                    case Light.INTENSITYMODE_LUMINANCE:
                        photometricScale = this.radius * this.radius;
                        break;
                }
                break;
            case Light.LIGHTTYPEID_DIRECTIONALLIGHT:
                switch (photometricMode) {
                    case Light.INTENSITYMODE_ILLUMINANCE:
                        photometricScale = 1.0;
                        break;
                    case Light.INTENSITYMODE_LUMINANCE:
                        // When radius (and therefore solid angle) is non-zero a directional lights brightness can be specified via central (peak) luminance.
                        // For a directional light the 'radius' defines the angular radius (in radians) rather than world-space radius (e.g. in metres).
                        var apexAngleRadians = this.radius;
                        // Impose a minimum light angular size to avoid the light becoming an infinitely small angular light source (i.e. a dirac delta function).
                        apexAngleRadians = Math.max(apexAngleRadians, 0.001);
                        var solidAngle = 2.0 * Math.PI * (1.0 - Math.cos(apexAngleRadians));
                        photometricScale = solidAngle;
                        break;
                }
                break;
            case Light.LIGHTTYPEID_HEMISPHERICLIGHT:
                // No fall off in hemisperic light.
                photometricScale = 1.0;
                break;
        }
        return photometricScale;
    };
    /**
     * Reorder the light in the scene according to their defined priority.
     * @hidden Internal Use Only
     */
    Light.prototype._reorderLightsInScene = function () {
        var scene = this.getScene();
        if (this._renderPriority != 0) {
            scene.requireLightSorting = true;
        }
        this.getScene().sortLightsByPriority();
    };
    /**
     * Falloff Default: light is falling off following the material specification:
     * standard material is using standard falloff whereas pbr material can request special falloff per materials.
     */
    Light.FALLOFF_DEFAULT = 0;
    /**
     * Falloff Physical: light is falling off following the inverse squared distance law.
     */
    Light.FALLOFF_PHYSICAL = 1;
    /**
     * Falloff gltf: light is falling off as described in the gltf moving to PBR document
     * to enhance interoperability with other engines.
     */
    Light.FALLOFF_GLTF = 2;
    /**
     * Falloff Standard: light is falling off like in the standard material
     * to enhance interoperability with other materials.
     */
    Light.FALLOFF_STANDARD = 3;
    //lightmapMode Consts
    /**
     * If every light affecting the material is in this lightmapMode,
     * material.lightmapTexture adds or multiplies
     * (depends on material.useLightmapAsShadowmap)
     * after every other light calculations.
     */
    Light.LIGHTMAP_DEFAULT = 0;
    /**
     * material.lightmapTexture as only diffuse lighting from this light
     * adds only specular lighting from this light
     * adds dynamic shadows
     */
    Light.LIGHTMAP_SPECULAR = 1;
    /**
     * material.lightmapTexture as only lighting
     * no light calculation from this light
     * only adds dynamic shadows from this light
     */
    Light.LIGHTMAP_SHADOWSONLY = 2;
    // Intensity Mode Consts
    /**
     * Each light type uses the default quantity according to its type:
     *      point/spot lights use luminous intensity
     *      directional lights use illuminance
     */
    Light.INTENSITYMODE_AUTOMATIC = 0;
    /**
     * lumen (lm)
     */
    Light.INTENSITYMODE_LUMINOUSPOWER = 1;
    /**
     * candela (lm/sr)
     */
    Light.INTENSITYMODE_LUMINOUSINTENSITY = 2;
    /**
     * lux (lm/m^2)
     */
    Light.INTENSITYMODE_ILLUMINANCE = 3;
    /**
     * nit (cd/m^2)
     */
    Light.INTENSITYMODE_LUMINANCE = 4;
    // Light types ids const.
    /**
     * Light type const id of the point light.
     */
    Light.LIGHTTYPEID_POINTLIGHT = 0;
    /**
     * Light type const id of the directional light.
     */
    Light.LIGHTTYPEID_DIRECTIONALLIGHT = 1;
    /**
     * Light type const id of the spot light.
     */
    Light.LIGHTTYPEID_SPOTLIGHT = 2;
    /**
     * Light type const id of the hemispheric light.
     */
    Light.LIGHTTYPEID_HEMISPHERICLIGHT = 3;
    __decorate([
        serializeAsColor3()
    ], Light.prototype, "diffuse", void 0);
    __decorate([
        serializeAsColor3()
    ], Light.prototype, "specular", void 0);
    __decorate([
        serialize()
    ], Light.prototype, "falloffType", void 0);
    __decorate([
        serialize()
    ], Light.prototype, "intensity", void 0);
    __decorate([
        serialize()
    ], Light.prototype, "range", null);
    __decorate([
        serialize()
    ], Light.prototype, "intensityMode", null);
    __decorate([
        serialize()
    ], Light.prototype, "radius", null);
    __decorate([
        serialize()
    ], Light.prototype, "_renderPriority", void 0);
    __decorate([
        expandToProperty("_reorderLightsInScene")
    ], Light.prototype, "renderPriority", void 0);
    __decorate([
        serialize("shadowEnabled")
    ], Light.prototype, "_shadowEnabled", void 0);
    __decorate([
        serialize("excludeWithLayerMask")
    ], Light.prototype, "_excludeWithLayerMask", void 0);
    __decorate([
        serialize("includeOnlyWithLayerMask")
    ], Light.prototype, "_includeOnlyWithLayerMask", void 0);
    __decorate([
        serialize("lightmapMode")
    ], Light.prototype, "_lightmapMode", void 0);
    return Light;
}(Node));
export { Light };
//# sourceMappingURL=light.js.map